import gql from 'graphql-tag';
import {
    ChannelOwner,
    ChannelSubscription,
    ChannelTypes,
    IdAndName,
} from '../shared/types';

export const CHANNEL_FIELDS_FRAGMENT = `
	_id
	name
	channelOrder {
		groupId
		order
	}
	ownerType
	ownerId
	owner {
		_id
		name
	}
	price
	description
	url
	profileImage
	coverImage
	isCurrentUserSubscribed
	defaultTrialDurationDays
	type
	hideDisclaimer
	createdAt
`;

export const GET_CHANNELS_AND_GROUPS = gql`
    query channelsAndGroups {
        channels {
            _id
            name
            channelOrder {
                groupId
                order
            }
            owner {
                _id
                name
            }
            coverImage
            type
            isCurrentUserSubscribed
        }
        channelGroups {
            _id
            name
            channelIds
        }
    }
`;

// Lean query for the Channel Groups admin page
export const GET_CHANNELS_AND_GROUPS_FOR_PAGE = gql`
    query channelsAndGroupsForPage {
        channels {
            _id
            name
            channelOrder {
                groupId
                order
            }
            owner {
                _id
                name
            }
        }
        channelGroups {
            _id
            name
            description
            weight
            channelIds
            channelsCount
            createdAt
        }
    }
`;

export const GET_PREMIUM_CHANNELS = gql`
    query premiumChannels {
        premiumChannels(excludeSubscriptions: true) {
            _id
            name
            owner {
                _id
                name
            }
            coverImage
            type
        }
    }
`;

export const GET_FREE_CHANNELS = gql`
    query freeChannels {
        freeChannels(excludeSubscriptions: true) {
            _id
            name
            owner {
                _id
                name
            }
            coverImage
            type
        }
    }
`;

export const GET_CHANNELS_INFO = gql`
    query channels {
        channels {
            _id
            name
        }
    }
`;

export const GET_CHANNELS_PUBLISHERS_DASHBOARD_SHORT = gql`
    query channels {
        channels {
            _id
            name
            ownerType
            ownerId
            owner {
                _id
                name
            }
        }
    }
`;

export const GET_CHANNELS_ADD_EDIT_ARTICLE = gql`
    query channels {
        channels {
            _id
            name
            type
            ownerType
            ownerId
            adminUsers {
                _id
            }
        }
    }
`;

export const GET_CHANNELS_PUBLISHERS_DASHBOARD = gql`
	query allChannels {
		allChannels {
			ownerType
			${CHANNEL_FIELDS_FRAGMENT}
			adminUsers {
				_id
			}
			channelActiveUsersCount
			articleCount
			archivedArticleCount
			createdAt
			createdBy
			state
		}
	}`;

// Lean query for the Channels list screen — requests only the fields rendered in
// the table. Avoids expensive field resolvers (isCurrentUserSubscribed,
// channelOrder, coverImage, profileImage, etc.) that are not displayed there.
export const GET_CHANNELS_LIST = gql`
    query allChannelsForList {
        allChannels {
            _id
            name
            ownerType
            owner {
                _id
                name
            }
            type
            adminUsers {
                _id
            }
            channelActiveUsersCount
            articleCount
            archivedArticleCount
            createdAt
            state
        }
    }
`;

// Fast initial load — all fields except expensive counts. Load counts separately
// via lazy loading to unblock UI faster.
export const GET_CHANNELS_LIST_LIGHT = gql`
    query allChannelsForList {
        allChannels {
            _id
            name
            ownerType
            owner {
                _id
                name
            }
            type
            adminUsers {
                _id
            }
            createdAt
            state
        }
    }
`;

// Lazy-loaded counts — fetch separately after initial page render to avoid
// blocking the UI. Call this after GET_CHANNELS_LIST_LIGHT completes.
export const GET_CHANNELS_COUNTS = gql`
    query allChannelsCountsForList {
        allChannels {
            _id
            channelActiveUsersCount
            articleCount
            archivedArticleCount
        }
    }
`;

export const ADD_PREMIUM_CHANNEL = gql`
	mutation addChannel($item: ChannelInputAdd) {
		addChannel(item: $item) {
			${CHANNEL_FIELDS_FRAGMENT}
			ownerId
			adminUsers {
				_id
				name
			}
		}
	}
`;

export const EDIT_PREMIUM_CHANNEL = gql`
	mutation editChannel($item: ChannelInputEdit) {
		editChannel(item: $item) {
			${CHANNEL_FIELDS_FRAGMENT}
			ownerId
			adminUsers {
				_id
				name
			}
		}
	}
`;

export const ARCHIVE_PREMIUM_CHANNEL = gql`
    mutation archiveChannel($id: ID!) {
        archiveChannel(id: $id) {
            _id
        }
    }
`;

export const GET_PREMIUM_CHANNEL_BY_ID = gql`
	query findChannelById($id: ID!) {
		channel: findChannelById(id: $id) {
			${CHANNEL_FIELDS_FRAGMENT}
			userChannelSubscription {
				cancelAtPeriodEnd
				cancelAt
				cancelRequestedAt
				subscribed
				subscriptionState
				subscriptionType
			}
		}
	}
`;

// Lean query for Premium Channel screens (payment/subscription) — only the fields
// actually displayed. Drops all fields except _id, name, price, hideDisclaimer.
export const GET_PREMIUM_CHANNEL_BY_ID_LEAN = gql`
    query findChannelById($id: ID!) {
        channel: findChannelById(id: $id) {
            _id
            name
            price
            hideDisclaimer
            userChannelSubscription {
                cancelAtPeriodEnd
                cancelAt
                cancelRequestedAt
                subscribed
                subscriptionState
                subscriptionType
            }
        }
    }
`;

export const GET_CHANNEL_AND_SUBSCRIPTION = gql`
	query findChannelById($id: ID!) {
		channel: findChannelById(id: $id) {
			${CHANNEL_FIELDS_FRAGMENT}
			userChannelSubscription {
				cancelAtPeriodEnd
				cancelAt
				cancelRequestedAt
				subscribed
				subscriptionState
				subscriptionType
				acknowledgedNonChargeSubExpired
			}
		}
	}
`;

// Lean query for Channel cover display — only the fields rendered in the cover card.
export const GET_CHANNEL_AND_SUBSCRIPTION_LEAN = gql`
    query findChannelById($id: ID!) {
        channel: findChannelById(id: $id) {
            _id
            name
            coverImage
            type
            profileImage
            ownerId
            ownerType
            owner {
                _id
                name
            }
            description
            url
            userChannelSubscription {
                cancelAtPeriodEnd
                cancelAt
                cancelRequestedAt
                subscribed
                subscriptionState
                subscriptionType
                acknowledgedNonChargeSubExpired
            }
        }
    }
`;

export const GET_CHANNEL_WITH_CATEGORIES = gql`
	query findChannelById($id: ID!) {
		channel: findChannelById(id: $id) {
			state
			${CHANNEL_FIELDS_FRAGMENT}
			categories{
				_id
				name
			}
			userChannelSubscription {
				cancelAtPeriodEnd
				cancelAt
				cancelRequestedAt
				subscribed
				subscriptionType
				subscriptionState
				acknowledgedNonChargeSubExpired
			}
		}
	}
`;

// Lean query for Channel page — only the fields rendered in the display and subscription status.
// Drops unused fields like channelOrder, ownerType, price, isCurrentUserSubscribed, etc.
export const GET_CHANNEL_WITH_CATEGORIES_LEAN = gql`
    query findChannelById($id: ID!) {
        channel: findChannelById(id: $id) {
            state
            _id
            name
            coverImage
            type
            profileImage
            ownerId
            ownerType
            owner {
                _id
                name
            }
            description
            url
            categories {
                _id
                name
            }
            userChannelSubscription {
                cancelAtPeriodEnd
                cancelAt
                cancelRequestedAt
                subscribed
                subscriptionType
                subscriptionState
                acknowledgedNonChargeSubExpired
            }
        }
    }
`;

interface ChannelFragmentResponse {
    _id: string;
    name: string;
    ownerType: ChannelOwner;
    ownerId: string;
    owner: IdAndName;
    price: number;
    description: string;
    url: string;
    profileImage: string;
    coverImage: string;
    isCurrentUserSubscribed: boolean;
}

export interface ChannelSubscribedUser {
    _id: string;
    name: string;
    email: string;
    channelSubscriptions: ChannelSubscription[];
}

export interface ChannelSubscriberAddEditFragment extends ChannelFragmentResponse {
    ownerId: string;
    glAccount: string;
    adminUsers: IdAndName[];
    defaultTrialDurationDays: number;
    channelSubscribedUsers: ChannelSubscribedUser[];
    type: ChannelTypes;
}

export interface ChannelSubscriberAddEditResponse {
    channel: ChannelSubscriberAddEditFragment;
}

export const GET_PREMIUM_CHANNEL_FOR_EDIT = gql`
    query findChannelById($id: ID!) {
        channel: findChannelById(id: $id) {
            _id
            name
            ownerType
            ownerId
            price
            description
            url
            profileImage
            coverImage
            defaultTrialDurationDays
            type
            hideDisclaimer
            glAccount
            articleCount
            templateCount
            channelActiveUsersCount
            state
            adminUsers {
                _id
                name
            }
        }
    }
`;

export const GET_PREMIUM_CHANNEL_BY_ID_PUBLISHERS_DASHBOARD = (
    params: FindChannelByIdParams
) => gql`
	query findChannelById($id: ID!) {
		channel: findChannelById(id: $id) {
			${CHANNEL_FIELDS_FRAGMENT}
			ownerId
			glAccount
			articleCount
			templateCount
			channelActiveUsersCount
			state
			adminUsers {
				_id
				name
			}
			channelSubscribedUsers(includePreviouslySubscribedUsers: ${params.includePreviouslySubscribedUsers}) {
				_id
				email
				name
				channelSubscriptions(channelId: $id) {
					subscriptionType
					cancelAt
					notes
					pricePerMonth
					subscriptionState
					isFullArticleContent
					accountNumber
					rebate
					billingMethod
					trialDuration
				}
			}
		}
	}
`;

export const GET_PREMIUM_CHANNEL_SUBSCRIBED_USERS_BY_ID = gql`
    query findChannelById($id: ID!) {
        channel: findChannelById(id: $id) {
            _id
            name
            type
            channelActiveUsersCount
            articleCount
            adminUsers {
                _id
            }
            defaultTrialDurationDays
            type
            state
            channelSubscribedUsers(includePreviouslySubscribedUsers: true) {
                _id
                name
                company
                email
                channelSubscriptions(channelId: $id) {
                    channelId
                    createdAt
                    updatedAt
                    subscriptionType
                    subscriptionState
                    emailNotifications
                    rebate
                    notes
                    cancelAt
                    cancelRequestedAt
                    cancelAtPeriodEnd
                    isFullArticleContent
                }
            }
        }
    }
`;

export const NOTIFY_REGISTERED_CHANNEL_INTEREST = gql`
    mutation notifyRegisteredChannelInterest(
        $item: NotifyRegisterChannelInterestInput!
    ) {
        notifyRegisteredChannelInterest(item: $item) {
            channelName
            userEmail
        }
    }
`;

export const PROCESS_TRIAL_REQUEST = gql`
    mutation processTrialRequest($item: ProcessTrialRequestInput!) {
        processTrialRequest(item: $item) {
            _id
        }
    }
`;

export const PROCESS_FREE_CLOSED_REQUEST = gql`
    mutation processFreeClosedRequest($item: ProcessTrialRequestInput!) {
        processFreeClosedRequest(item: $item) {
            _id
            name
        }
    }
`;

export interface ProcessTrialRequestInput {
    userId: string;
    channelId: string;
    approved: boolean;
}

export interface FindChannelByIdParams {
    includePreviouslySubscribedUsers: boolean;
}
